import { useEffect, useState } from 'react';

import type { AdminFile } from '@mds/shared/types/file';

import * as files from './files.api';
import { uploadPreview, uploadVariants } from './upload';

/**
 * Files this session has already tried. A PDF that will not render is not
 * downloaded again on every visit; a reload gives it another go.
 */
const attempted = new Set<string>();

const usable = (file: AdminFile) => file.uploadStatus === 'ready' && !file.deletedAt;
const needsPreview = (file: AdminFile) => usable(file) && !file.thumbnailUrl;
const needsVariants = (file: AdminFile) =>
  usable(file) && file.mimeType !== 'application/pdf' && file.variantWidths.length === 0;

/**
 * Makes what the files on screen are missing: a preview, and for an image the
 * display sizes public pages are served. That is everything uploaded before
 * either existed, and any upload whose own attempt failed. One file at a time,
 * in the background, each downloaded once for both; a file that still cannot be
 * drawn keeps its type icon, and an image without sizes is served whole.
 *
 * Returns the preview URLs made so far, by file ID, for the page to lay over
 * the list rather than refetching it after each one. Display sizes are not
 * shown in the list, so there is nothing to lay over for them.
 */
export function useImageBackfill(
  rows: readonly AdminFile[],
  enabled: boolean,
): Readonly<Record<string, string>> {
  const [made, setMade] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!enabled) return;
    const queue = rows.filter(
      (file) => !attempted.has(file.id) && (needsPreview(file) || needsVariants(file)),
    );
    if (queue.length === 0) return;

    let stopped = false;
    void (async () => {
      for (const file of queue) {
        // A newer list has taken over; it picks up whatever this one did not reach.
        if (stopped) return;
        if (attempted.has(file.id)) continue;
        attempted.add(file.id);
        try {
          const source = await files.fetchContent(file.id);
          if (needsPreview(file)) {
            const { thumbnailUrl } = await uploadPreview(file.id, source, file.mimeType).catch(
              () => ({ thumbnailUrl: null }),
            );
            if (thumbnailUrl) setMade((current) => ({ ...current, [file.id]: thumbnailUrl }));
          }
          if (needsVariants(file)) await uploadVariants(file.id, source);
        } catch {
          // Decoration and speed only: the file itself is untouched.
        }
      }
    })();
    return () => {
      stopped = true;
    };
  }, [rows, enabled]);

  return made;
}
